import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  Tooltip,
  IconButton,
  TextField
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Timeline as TimelineIcon,
  CalendarToday as CalendarIcon,
  Speed as SpeedIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  DateRange as DateRangeIcon,
  Today as TodayIcon
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import axios from 'axios';
import Header from '../../components/Header';

const ClientProductAnalysis = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // URLパラメータから取得
  const clientId = searchParams.get('clientId');
  const clientName = searchParams.get('clientName');
  const productId = searchParams.get('productId');
  const productName = searchParams.get('productName');

  // 状態管理
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // 期間管理（デフォルト：過去6ヶ月）
  const getDefaultDateRange = () => {
    const now = new Date();
    const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1);
    const endOfCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    return {
      startDate: `${sixMonthsAgo.getFullYear()}-${String(sixMonthsAgo.getMonth() + 1).padStart(2, '0')}-01`,
      endDate: `${endOfCurrentMonth.getFullYear()}-${String(endOfCurrentMonth.getMonth() + 1).padStart(2, '0')}-${endOfCurrentMonth.getDate()}`
    };
  };

  const [dateRange, setDateRange] = useState(getDefaultDateRange());

  // 期間プリセット
  const handlePeriodPreset = (type) => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    
    switch (type) {
      case 'thisMonth':
        setDateRange({
          startDate: `${year}-${String(month + 1).padStart(2, '0')}-01`,
          endDate: `${year}-${String(month + 1).padStart(2, '0')}-${new Date(year, month + 1, 0).getDate()}`
        });
        break;
      case 'lastMonth':
        const lastMonth = month === 0 ? 11 : month - 1;
        const lastMonthYear = month === 0 ? year - 1 : year;
        setDateRange({
          startDate: `${lastMonthYear}-${String(lastMonth + 1).padStart(2, '0')}-01`,
          endDate: `${lastMonthYear}-${String(lastMonth + 1).padStart(2, '0')}-${new Date(lastMonthYear, lastMonth + 1, 0).getDate()}`
        });
        break;
      case 'last3Months':
        const threeMonthsAgo = new Date(year, month - 2, 1);
        setDateRange({
          startDate: `${threeMonthsAgo.getFullYear()}-${String(threeMonthsAgo.getMonth() + 1).padStart(2, '0')}-01`,
          endDate: `${year}-${String(month + 1).padStart(2, '0')}-${new Date(year, month + 1, 0).getDate()}`
        });
        break;
      case 'last6Months':
        setDateRange(getDefaultDateRange());
        break;
      default:
        break;
    }
  };

  // データ取得
  const fetchAnalysisData = useCallback(async (showRefreshLoader = false) => {
    if (showRefreshLoader) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const token = sessionStorage.getItem('token');
      
      const response = await axios.get('/api/analysis/client-product-detail', {
        params: {
          clientId: clientId,
          productId: productId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      setAnalysisData(response.data);
      setError('');

    } catch (err) {
      console.error('データ取得エラー:', err);
      if (err.response?.status === 403) {
        setError('この顧客・製品にアクセスする権限がありません');
      } else if (err.response?.status === 500) {
        setError('サーバーエラーが発生しました');
      } else {
        setError('分析データの取得に失敗しました');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [clientId, productId, dateRange]);

  useEffect(() => {
    if (clientId && productId) {
      fetchAnalysisData();
    } else {
      setError('必要なパラメータが不足しています');
      setLoading(false);
    }
  }, [clientId, productId, dateRange, fetchAnalysisData]);

  // 戻る処理
  const handleGoBack = () => {
    navigate('/analysis');
  };

  // 更新処理
  const handleRefresh = () => {
    fetchAnalysisData(true);
  };

  // トレンド表示の計算
  const getTrendInfo = (trend) => {
    switch (trend) {
      case 'increasing':
        return { label: '増加傾向', color: 'error', icon: '📈' };
      case 'decreasing':
        return { label: '減少傾向', color: 'success', icon: '📉' };
      default:
        return { label: '安定', color: 'info', icon: '➡️' };
    }
  };

  // ローディング画面
  if (loading) {
    return (
      <>
        <Header />
        <Container maxWidth="md" sx={{ pt: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h6" color="text.secondary">
              分析データを読み込み中...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {clientName} × {productName}
            </Typography>
          </Box>
        </Container>
      </>
    );
  }

  // エラー画面
  if (error) {
    return (
      <>
        <Header />
        <Container maxWidth="md" sx={{ pt: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={handleGoBack}>
            戻る
          </Button>
        </Container>
      </>
    );
  }

  const trendInfo = getTrendInfo(analysisData?.metrics?.trend);

  return (
    <>
      <Header />
      <Container maxWidth="lg" sx={{ 
        pt: 3, 
        pb: { xs: 12, sm: 6 },
        minHeight: { xs: 'calc(100vh - 200px)', sm: 'auto' }
      }}>
        {/* ヘッダーセクション */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
            size={isMobile ? 'small' : 'medium'}
          >
            戻る
          </Button>
          <Tooltip title="データを更新">
            <IconButton 
              onClick={handleRefresh} 
              disabled={refreshing}
              sx={{ ml: 2 }}
            >
              <RefreshIcon sx={{ 
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* タイトルセクション */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <AssessmentIcon sx={{ fontSize: 32 }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              分析結果
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label={`顧客: ${clientName}`}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
            <Chip
              label={`製品: ${productName}`}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
            <Chip
              label={`期間: ${dateRange.startDate} 〜 ${dateRange.endDate}`}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
          </Box>
        </Paper>

        {/* 期間選択 */}
        <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <DateRangeIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                分析期間
              </Typography>
            </Box>
            
            <Grid container spacing={3}>
              {/* 期間プリセットボタン */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  期間プリセット
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<TodayIcon />}
                    onClick={() => handlePeriodPreset('thisMonth')}
                  >
                    今月
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handlePeriodPreset('lastMonth')}
                  >
                    先月
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handlePeriodPreset('last3Months')}
                  >
                    過去3ヶ月
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handlePeriodPreset('last6Months')}
                  >
                    過去6ヶ月
                  </Button>
                </Box>
              </Grid>
              
              {/* 手動期間選択 */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  カスタム期間
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="開始日"
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange(prev => ({
                      ...prev,
                      startDate: e.target.value
                    }))}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <TextField
                    label="終了日"
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange(prev => ({
                      ...prev,
                      endDate: e.target.value
                    }))}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* メトリクスカード */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: 'primary.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}
                  >
                    <TrendingUpIcon color="primary" />
                  </Box>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    累計供給量
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                  {analysisData?.metrics?.totalAmount?.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  リットル
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: 'success.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}
                  >
                    <CalendarIcon color="success" />
                  </Box>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    月平均供給量
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                  {analysisData?.metrics?.monthlyAverage?.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  リットル/月
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: 'warning.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}
                  >
                    <SpeedIcon color="warning" />
                  </Box>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    日平均消費量
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                  {Math.round(analysisData?.metrics?.dailyConsumptionRate || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  リットル/日
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: 'info.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}
                  >
                    <TimelineIcon color="info" />
                  </Box>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    消費傾向
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {trendInfo.icon}
                  </Typography>
                  <Chip
                    label={trendInfo.label}
                    color={trendInfo.color}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* グラフセクション */}
        <Grid container spacing={3}>
          {/* 月別供給量（線グラフ） */}
          <Grid item xs={12} lg={8}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <TimelineIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    月別供給量推移
                  </Typography>
                  <Tooltip title="月ごとの供給量の変化を表示">
                    <IconButton size="small" sx={{ ml: 1 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={analysisData?.monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="displayMonth" 
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        label={{ value: '供給量 (L)', angle: -90, position: 'insideLeft' }}
                      />
                      <RechartsTooltip
                        formatter={(value) => [`${value.toLocaleString()}L`, '供給量']}
                        labelFormatter={(label) => `期間: ${label}`}
                      />
                      <Line
                        type="monotone"
                        dataKey="amount"
                        stroke={theme.palette.primary.main}
                        strokeWidth={3}
                        dot={{ r: 6, fill: theme.palette.primary.main }}
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* 月別供給量（棒グラフ） */}
          <Grid item xs={12} lg={4}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <TrendingUpIcon color="secondary" sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    月別比較
                  </Typography>
                </Box>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analysisData?.monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="month" 
                        tick={{ fontSize: 10 }}
                        angle={-45}
                        textAnchor="end"
                        height={50}
                      />
                      <YAxis 
                        tick={{ fontSize: 10 }}
                        width={40}
                      />
                      <RechartsTooltip
                        formatter={(value) => [`${value.toLocaleString()}L`, '供給量']}
                      />
                      <Bar
                        dataKey="amount"
                        fill={theme.palette.secondary.main}
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 詳細情報 */}
        <Card elevation={2} sx={{ mt: 3, borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 3 }}>
              詳細分析
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2 }}>
                    供給量統計
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">最大月間供給量:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {analysisData?.metrics?.maxMonthlyAmount?.toLocaleString()}L
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">最小月間供給量:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {analysisData?.metrics?.minMonthlyAmount?.toLocaleString()}L
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">変動幅:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {((analysisData?.metrics?.maxMonthlyAmount || 0) - (analysisData?.metrics?.minMonthlyAmount || 0))?.toLocaleString()}L
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2 }}>
                    分析期間
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">開始:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {analysisData?.period?.start}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">終了:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {analysisData?.period?.end}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">期間:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {analysisData?.period?.months}ヶ月
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* データがない場合の表示 */}
            {(!analysisData?.monthlyData || analysisData.monthlyData.length === 0) && (
              <Box sx={{ textAlign: 'center', py: 4, mt: 3 }}>
                <Typography variant="body1" color="text.secondary">
                  選択した期間にデータがありません
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  期間を変更するか、供給データを登録してください
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </>
  );
};

export default ClientProductAnalysis;